import React, { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "../config";

// Donors (db/sritaru-donor-id.sql, db/sritaru-donor-admin.sql). Bhaskar, 2026-11-02: one person
// can end up on two Wall cards (a 2024 partner-list name and their later website gifts, or two
// emails), so admins merge them here. Every merge and move can be undone.

const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
const fullName = (c) => norm(`${c.first_name || ""} ${c.last_name || ""}`);
const phone10 = (s) => (s || "").replace(/\D/g, "").slice(-10);
const donorIdOf = (c) => c.donor_id || `row:${c.contribution_id}`;
const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
const treesText = (n) => `${n} tree${Number(n) === 1 ? "" : "s"}`;
// Same rule as the Wall: one donor's rows from the same day (undated: same year) are one contribution.
const giftKey = (c) => (c.created_at ? c.created_at.slice(0, 10) : `year:${c.year || ""}`);
const pairKey = (a, b) => [a, b].sort().join("|");

const DISMISSED_KEY = "sritaru-admin-not-same-donors";
const loadDismissed = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]"));
  } catch {
    return new Set();
  }
};
const saveDismissed = (set) => {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...set]));
  } catch {
    // Private window: the "not the same" list lasts until the page closes.
  }
};

function groupDonors(rows) {
  const byId = new Map();
  rows.forEach((c) => {
    const id = donorIdOf(c);
    if (!byId.has(id)) byId.set(id, []);
    byId.get(id).push(c);
  });
  return [...byId.entries()].map(([id, items]) => {
    items.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    const uniq = (f) => [...new Set(items.map(f).filter(Boolean))];
    return {
      id,
      items,
      name: `${items[0].first_name || ""} ${items[0].last_name || ""}`.trim(),
      fullNames: uniq(fullName),
      emails: uniq((c) => norm(c.email)),
      phones: uniq((c) => phone10(c.whatsapp)),
      teams: uniq((c) => c.team_name),
      years: uniq((c) => c.year).sort(),
      gifts: new Set(items.map(giftKey)).size,
      trees: items.reduce((s, c) => s + (Number(c.num_trees) || 0), 0),
    };
  });
}

// Pairs of cards that may be one person. Admins decide; nothing merges by itself.
function suggestPairs(donors, dismissed) {
  const pairs = new Map();
  const add = (a, b, reason) => {
    if (a.id === b.id) return;
    const k = pairKey(a.id, b.id);
    if (dismissed.has(k)) return;
    if (!pairs.has(k)) pairs.set(k, { key: k, a, b, reasons: new Set() });
    pairs.get(k).reasons.add(reason);
  };
  const index = (keyOf) => {
    const m = new Map();
    donors.forEach((d) => keyOf(d).forEach((k) => {
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(d);
    }));
    return m;
  };
  const each = (m, reason) => m.forEach((ds) => ds.forEach((a, i) => ds.slice(i + 1).forEach((b) => add(a, b, reason))));

  // Same full name; a single word ("Srinivas") is too common to count.
  each(index((d) => d.fullNames.filter((n) => n.includes(" "))), "same full name");
  each(index((d) => d.phones), "same phone");
  // A card with only a first name and no email or phone, next to a fuller card with that first name.
  const byFirst = index((d) => d.fullNames.map((n) => n.split(" ")[0]));
  donors
    .filter((d) => !d.emails.length && !d.phones.length && d.fullNames.every((n) => !n.includes(" ")))
    .forEach((a) => (byFirst.get(a.fullNames[0]) || []).forEach((b) => add(a, b, "first name only")));

  const rank = (p) => (p.reasons.has("same phone") ? 0 : p.reasons.has("same full name") ? 1 : 2);
  return [...pairs.values()].sort((x, y) => rank(x) - rank(y) || x.a.name.localeCompare(y.a.name));
}

function DonorSummary({ d }) {
  return (
    <div className="text-sm">
      <div className="font-semibold text-sritaru-800">{d.name || "(no name)"}</div>
      <div className="text-gray-600">
        {d.gifts} contribution{d.gifts > 1 ? "s" : ""}
        {d.items.length > d.gifts ? ` (${d.items.length} names)` : ""} · {treesText(d.trees)}
        {d.years.length ? ` · ${d.years.join(", ")}` : ""}
        {d.teams.length ? ` · ${d.teams.join(", ")}` : ""}
      </div>
      {d.emails.length > 0 && <div className="text-gray-500 break-all">{d.emails.join(", ")}</div>}
      {d.phones.length > 0 && <div className="text-gray-500">{d.phones.join(", ")}</div>}
      {d.fullNames.length > 1 && <div className="text-gray-500">Also as: {d.fullNames.slice(1).join(", ")}</div>}
    </div>
  );
}

function AdminDonors({ password }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [dismissed, setDismissed] = useState(loadDismissed);
  const [showSuggestions, setShowSuggestions] = useState(true);
  // The last change, kept so it can be undone: [{ ids, from }] puts each set of rows back on its old card.
  const [lastAction, setLastAction] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("sritaru_admin_list", { p_password: password, p_archived: false });
    setLoading(false);
    if (error) return alert("Error loading contributions: " + error.message);
    setRows(data || []);
  }, [password]);

  useEffect(() => {
    load();
  }, [load]);

  const donors = useMemo(() => groupDonors(rows), [rows]);
  const byId = useMemo(() => new Map(donors.map((d) => [d.id, d])), [donors]);
  const suggestions = useMemo(() => suggestPairs(donors, dismissed), [donors, dismissed]);

  const setDonor = async (ids, donorId) => {
    const { data, error } = await supabase.rpc("sritaru_admin_set_donor", {
      p_password: password,
      p_contribution_ids: ids,
      p_donor_id: donorId && !donorId.startsWith("row:") ? donorId : null,
    });
    if (error) throw error;
    return data;
  };

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setBusy(false);
      load();
    }
  };

  // The card with the most contributions keeps its id; the others' rows move onto it.
  const merge = (list) =>
    run(async () => {
      const [keep, ...rest] = [...list].sort((a, b) => b.items.length - a.items.length);
      const moves = rest.map((d) => ({ ids: d.items.map((c) => String(c.contribution_id)), from: d.id }));
      for (const m of moves) await setDonor(m.ids, keep.id);
      setLastAction({ label: `Merged ${list.map((d) => d.name).join(" + ")} into one card`, moves });
      setSelected([]);
    });

  const moveOut = (d, c) =>
    run(async () => {
      const ids = [String(c.contribution_id)];
      await setDonor(ids, null);
      setLastAction({ label: `Moved the ${formatDate(c.created_at) || c.year || "undated"} gift of ${d.name} (${treesText(c.num_trees)}) to its own card`, moves: [{ ids, from: d.id }] });
    });

  const undo = () =>
    run(async () => {
      for (const m of lastAction.moves) await setDonor(m.ids, m.from);
      setLastAction(null);
    });

  const notSame = (p) => {
    const next = new Set(dismissed).add(p.key);
    setDismissed(next);
    saveDismissed(next);
  };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const q = norm(query);
  const listed = donors
    .filter((d) => !q || [d.name, ...d.fullNames, ...d.emails, ...d.phones].some((s) => norm(s).includes(q)))
    .sort((a, b) => a.name.localeCompare(b.name));

  const btn = "px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-50";

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        One card per donor on the Wall. {donors.length} donors, {rows.length} contributions. Merge cards that are the same
        person; every change can be undone.
      </p>

      {lastAction && (
        <div className="flex flex-wrap items-center gap-3 bg-sritaru-50 border border-sritaru-200 rounded-xl px-4 py-3 text-sm">
          <span className="text-sritaru-800">{lastAction.label}.</span>
          <button onClick={undo} disabled={busy} className={`${btn} bg-white border border-sritaru-300 text-sritaru-700`}>
            Undo
          </button>
        </div>
      )}

      <section className="bg-white rounded-xl border border-gray-200 p-4">
        <button
          onClick={() => setShowSuggestions(!showSuggestions)}
          className="w-full flex justify-between items-center text-left"
          aria-expanded={showSuggestions}
        >
          <h2 className="text-lg font-bold text-sritaru-800">Possibly the same person ({suggestions.length})</h2>
          <span className="text-sritaru-600 text-sm">{showSuggestions ? "Hide ▴" : "Show ▾"}</span>
        </button>
        {showSuggestions && (
          <div className="mt-3 space-y-3">
            {suggestions.length === 0 && <p className="text-sm text-gray-500">Nothing to check.</p>}
            {suggestions.map((p) => (
              <div key={p.key} className="border border-gray-200 rounded-lg p-3">
                <div className="text-xs font-semibold text-amber-700 mb-2">{[...p.reasons].join(" · ")}</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <DonorSummary d={p.a} />
                  <DonorSummary d={p.b} />
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => merge([p.a, p.b])} disabled={busy} className={`${btn} bg-sritaru-700 text-white hover:bg-sritaru-800`}>
                    Same person: merge
                  </button>
                  <button onClick={() => notSame(p)} disabled={busy} className={`${btn} bg-white border border-gray-300 text-gray-700`}>
                    Not the same
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap gap-3 items-center mb-3">
          <h2 className="text-lg font-bold text-sritaru-800 mr-auto">All donors</h2>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email or phone"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full sm:w-72"
          />
        </div>

        {selected.length > 0 && (
          <div className="sticky top-16 z-10 flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 text-sm">
            <span>
              Selected: {selected.map((id) => byId.get(id)?.name).filter(Boolean).join(", ")}
            </span>
            <button
              onClick={() => merge(selected.map((id) => byId.get(id)).filter(Boolean))}
              disabled={busy || selected.length < 2}
              className={`${btn} bg-sritaru-700 text-white hover:bg-sritaru-800`}
            >
              Merge {selected.length} into one
            </button>
            <button onClick={() => setSelected([])} className={`${btn} bg-white border border-gray-300 text-gray-700`}>
              Clear
            </button>
          </div>
        )}

        {loading && <p className="text-sm text-gray-500">Loading…</p>}
        <ul className="divide-y divide-gray-100">
          {listed.map((d) => (
            <li key={d.id} className="py-2">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selected.includes(d.id)}
                  onChange={() => toggle(d.id)}
                  aria-label={`Select ${d.name}`}
                />
                <button onClick={() => setOpenId(openId === d.id ? null : d.id)} className="flex-1 text-left" aria-expanded={openId === d.id}>
                  <DonorSummary d={d} />
                </button>
              </div>
              {openId === d.id && (
                <ol className="mt-2 ml-7 space-y-1">
                  {d.items.map((c) => (
                    <li key={c.contribution_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm bg-gray-50 rounded px-3 py-2">
                      <span className="text-gray-500 w-24">{formatDate(c.created_at) || c.year || "undated"}</span>
                      <span className="font-semibold">{treesText(c.num_trees)}</span>
                      <span className="text-gray-600">{c.payment_status}</span>
                      {c.team_name && <span className="text-gray-600">{c.team_name}</span>}
                      {c.dedicated_to && <span className="italic text-gray-500">for {c.dedicated_to}</span>}
                      <span className="text-gray-500">{fullName(c)}</span>
                      {d.items.length > 1 && (
                        <button
                          onClick={() => moveOut(d, c)}
                          disabled={busy}
                          className="ml-auto text-xs text-sritaru-700 underline disabled:opacity-50"
                        >
                          Move to its own card
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export default AdminDonors;
